import { useMemo, useState } from "react"
import {
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    type ColumnDef,
    type SortingState,
    type VisibilityState,
} from "@tanstack/react-table"
import { AlertTriangle, ArrowUpDown, CheckCircle2, Clock, Columns2, Eye, Filter, Search, X } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import {
    Table, TableBody, TableCell, TableHead,
    TableHeader, TableRow,
} from "@/components/ui/table"
import {
    Pagination, PaginationContent, PaginationEllipsis,
    PaginationItem, PaginationLink, PaginationNext, PaginationPrevious,
} from "@/components/ui/pagination"
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip"
import {
    Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover"
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card"
import type { Instrument } from "@/Wist-blockchain/collections/instruments/types"
import { useNavigate } from "@tanstack/react-router"

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; badgeCls: string; chipCls: string }> = {
    operativo: {
        label: "Operativo",
        badgeCls: "bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800",
        chipCls:  "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-700",
    },
    operativo_observaciones: {
        label: "Op. con observaciones",
        badgeCls: "bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800",
        chipCls:  "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700",
    },
    fuera_servicio: {
        label: "Fuera de servicio",
        badgeCls: "bg-red-100 text-red-600 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800",
        chipCls:  "bg-red-100 text-red-600 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700",
    },
}

// ─── Equipment type labels ────────────────────────────────────────────────────

const EQUIPMENT_TYPE_LABELS: Record<string, string> = {
    farmaceutico:      "Farmacéutico",
    industrial:        "Industrial",
    medico:            "Médico",
    alimentos_bebidas: "Alimentos y Bebidas",
    laboratorio:       "Laboratorio",
    mineria:           "Minería",
    construccion:      "Construcción",
    ambiental:         "Ambiental",
    otro:              "Otro",
}

const COLUMN_LABELS: Record<string, string> = {
    serialNumber:      "N° Serial",
    equipmentName:     "Nombre",
    equipmentType:     "Tipo",
    idBrand:           "Marca",
    idOrganization:    "Cliente",
    status:            "Estado",
    lastService:       "Último servicio",
    calibrationStatus: "Calificación",
    actions:           "Ver",
}

type CalibrationStatus = "overdue" | "soon" | "ok" | "unknown"

function getCalibrationStatus(dateStr: string | undefined): CalibrationStatus {
    if (!dateStr) return "unknown"
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const date = new Date(`${dateStr.split("T")[0]}T12:00:00`)
    const diffDays = Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    if (diffDays < 0)  return "overdue"
    if (diffDays <= 30) return "soon"
    return "ok"
}

const CALIBRATION_BADGE: Record<CalibrationStatus, { label: string; cls: string; Icon: React.ElementType } | null> = {
    overdue: { label: "Vencida",  cls: "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800",         Icon: AlertTriangle },
    soon:    { label: "Próxima",  cls: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800", Icon: Clock },
    ok:      { label: "Al día",   cls: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800", Icon: CheckCircle2 },
    unknown: null,
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function TruncatedCell({ value, maxLength = 28, className = "" }: {
    value: string; maxLength?: number; className?: string
}) {
    const isTruncated = value?.length > maxLength
    const display = isTruncated ? value.slice(0, maxLength) + "…" : value
    if (!isTruncated) return <span className={className}>{display}</span>
    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className={`cursor-default ${className}`}>{display}</span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs wrap-break-word">{value}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
}

function buildPageRange(current: number, total: number): (number | "ellipsis")[] {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
    const pages: (number | "ellipsis")[] = [1]
    if (current > 3) pages.push("ellipsis")
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i)
    if (current < total - 2) pages.push("ellipsis")
    pages.push(total)
    return pages
}

// ─── Columns ──────────────────────────────────────────────────────────────────

function getColumns(
    navigate: ReturnType<typeof useNavigate>,
    brandMap: Map<string, string>,
    orgMap: Map<string, string>,
    maintenanceMap: Map<string, string>,
    lastServiceMap: Map<string, string>,
): ColumnDef<Instrument>[] {
    return [
        {
            id: "actions",
            header: "",
            cell: ({ row }) => (
                <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1.5 text-xs cursor-pointer whitespace-nowrap"
                    onClick={() => navigate({ to: "/instruments/$id", params: { id: row.original.$id } })}
                >
                    <Eye className="h-3.5 w-3.5" />
                    Ver
                </Button>
            ),
            size: 80, minSize: 80, maxSize: 80,
            enableSorting: false, enableHiding: false,
        },
        {
            accessorKey: "serialNumber",
            header: ({ column }) => (
                <Button variant="ghost" size="sm" className="-ml-3 h-8 whitespace-nowrap"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
                    N° Serial <ArrowUpDown className="ml-2 h-3.5 w-3.5 text-muted-foreground/70" />
                </Button>
            ),
            cell: ({ row }) => (
                <span className="font-mono text-sm font-medium tracking-tight whitespace-nowrap">
                    {row.getValue("serialNumber")}
                </span>
            ),
            size: 130, minSize: 100, maxSize: 160,
        },
        {
            accessorKey: "equipmentName",
            header: "Nombre",
            cell: ({ row }) => (
                <TruncatedCell value={row.getValue("equipmentName") ?? "—"} maxLength={32} className="text-sm" />
            ),
            size: 220, minSize: 140,
        },
        {
            accessorKey: "equipmentType",
            header: ({ column }) => (
                <Button variant="ghost" size="sm" className="-ml-3 h-8 whitespace-nowrap hidden sm:flex"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
                    Tipo <ArrowUpDown className="ml-2 h-3.5 w-3.5 text-muted-foreground/70" />
                </Button>
            ),
            cell: ({ row }) => (
                <span className="hidden sm:block">
                    <TruncatedCell
                        value={EQUIPMENT_TYPE_LABELS[row.getValue<string>("equipmentType")] ?? "—"}
                        maxLength={18} className="text-sm text-muted-foreground" />
                </span>
            ),
            size: 120, minSize: 100,
        },
        {
            accessorKey: "idBrand",
            header: ({ column }) => (
                <Button variant="ghost" size="sm" className="-ml-3 h-8 whitespace-nowrap hidden md:flex"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
                    Marca <ArrowUpDown className="ml-2 h-3.5 w-3.5 text-muted-foreground/70" />
                </Button>
            ),
            cell: ({ row }) => (
                <span className="hidden md:block">
                    <TruncatedCell value={brandMap.get(row.getValue("idBrand")) ?? "—"}
                        maxLength={18} className="text-sm text-muted-foreground" />
                </span>
            ),
            size: 130, minSize: 100,
        },
        {
            accessorKey: "idOrganization",
            header: ({ column }) => (
                <Button variant="ghost" size="sm" className="-ml-3 h-8 whitespace-nowrap"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
                    Cliente <ArrowUpDown className="ml-2 h-3.5 w-3.5 text-muted-foreground/70" />
                </Button>
            ),
            cell: ({ row }) => (
                <TruncatedCell value={orgMap.get(row.getValue("idOrganization")) ?? "—"}
                    maxLength={22} className="text-sm text-muted-foreground" />
            ),
            size: 160, minSize: 120,
        },
        {
            accessorKey: "status",
            header: ({ column }) => (
                <Button variant="ghost" size="sm" className="-ml-3 h-8 whitespace-nowrap"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
                    Estado <ArrowUpDown className="ml-2 h-3.5 w-3.5 text-muted-foreground/70" />
                </Button>
            ),
            cell: ({ row }) => {
                const raw = row.getValue<string>("status")
                const cfg = STATUS_CONFIG[raw]
                if (!cfg) return <span className="text-muted-foreground text-sm">—</span>
                return <Badge className={`${cfg.badgeCls} whitespace-nowrap text-xs`}>{cfg.label}</Badge>
            },
            size: 120, minSize: 80, maxSize: 130,
        },
        {
            id: "lastService",
            header: "Último servicio",
            cell: ({ row }) => {
                const dateStr = lastServiceMap.get(row.original.$id)
                if (!dateStr) return <span className="text-muted-foreground text-xs">Sin registros</span>
                const formatted = new Date(`${dateStr.split("T")[0]}T12:00:00`)
                    .toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })
                return <span className="text-sm text-muted-foreground whitespace-nowrap">{formatted}</span>
            },
            size: 130, minSize: 110,
            enableSorting: false,
        },
        {
            id: "calibrationStatus",
            header: "Calificación",
            cell: ({ row }) => {
                const dateStr = maintenanceMap.get(row.original.$id)
                const qualificationStatus = getCalibrationStatus(dateStr)
                const badgeCfg = CALIBRATION_BADGE[qualificationStatus]
                const formattedDate = dateStr
                    ? new Date(`${dateStr.split("T")[0]}T12:00:00`).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })
                    : null
                if (!badgeCfg) return <span className="text-muted-foreground text-xs">—</span>
                const StatusIcon = badgeCfg.Icon as React.FC<{ className?: string }>
                return (
                    <div className="flex flex-col gap-0.5">
                        <Badge className={`${badgeCfg.cls} whitespace-nowrap text-xs gap-1 border w-fit`}>
                            <StatusIcon className="h-3 w-3" />
                            {badgeCfg.label}
                        </Badge>
                        {formattedDate && (
                            <span className="text-[10px] text-muted-foreground whitespace-nowrap">{formattedDate}</span>
                        )}
                    </div>
                )
            },
            size: 130, minSize: 110, maxSize: 150,
            enableSorting: false,
        },
    ]
}

// ─── Component ────────────────────────────────────────────────────────────────

export function InstrumentsTable({
    instruments,
    brandMap,
    orgMap,
    maintenanceMap,
    lastServiceMap,
}: {
    instruments: Instrument[]
    brandMap: Map<string, string>
    orgMap: Map<string, string>
    maintenanceMap: Map<string, string>
    lastServiceMap: Map<string, string>
}) {
    const [sorting, setSorting] = useState<SortingState>([])
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})

    // Filter state
    const [searchText, setSearchText] = useState("")
    const [activeStatuses, setActiveStatuses] = useState<string[]>([])
    const [selectedType, setSelectedType] = useState("all")
    const [selectedOrg, setSelectedOrg] = useState("all")
    const [selectedCalibration, setSelectedCalibration] = useState<CalibrationStatus | "all">("all")

    const navigate = useNavigate()
    const columns = getColumns(navigate, brandMap, orgMap, maintenanceMap, lastServiceMap)

    const overdueCount = useMemo(() =>
        instruments.filter(i => getCalibrationStatus(maintenanceMap.get(i.$id)) === "overdue").length,
        [instruments, maintenanceMap]
    )

    // Unique types and orgs present in the full list
    const availableTypes = useMemo(() => {
        const seen = new Set<string>()
        instruments.forEach(i => { if (i.equipmentType) seen.add(i.equipmentType) })
        return Array.from(seen).sort()
    }, [instruments])

    const availableOrgs = useMemo(() => {
        const seen = new Set<string>()
        instruments.forEach(i => { if (i.idOrganization) seen.add(i.idOrganization) })
        return Array.from(seen)
            .map(id => ({ id, name: orgMap.get(id) ?? id }))
            .sort((a, b) => a.name.localeCompare(b.name))
    }, [instruments, orgMap])

    // Pre-filter data (TanStack Table handles sort + pagination on top)
    const filteredData = useMemo(() => {
        const q = searchText.trim().toLowerCase()
        return instruments.filter(inst => {
            if (q) {
                const hit =
                    (inst.serialNumber ?? "").toLowerCase().includes(q) ||
                    (inst.equipmentName ?? "").toLowerCase().includes(q) ||
                    (brandMap.get(inst.idBrand ?? "") ?? "").toLowerCase().includes(q) ||
                    (orgMap.get(inst.idOrganization ?? "") ?? "").toLowerCase().includes(q)
                if (!hit) return false
            }
            if (activeStatuses.length > 0 && !activeStatuses.includes(inst.status ?? "")) return false
            if (selectedType !== "all" && inst.equipmentType !== selectedType) return false
            if (selectedOrg !== "all" && inst.idOrganization !== selectedOrg) return false
            if (selectedCalibration !== "all" && getCalibrationStatus(maintenanceMap.get(inst.$id)) !== selectedCalibration) return false
            return true
        })
    }, [instruments, searchText, activeStatuses, selectedType, selectedOrg, selectedCalibration, brandMap, orgMap, maintenanceMap])

    const activeFilterCount =
        (searchText.trim() ? 1 : 0) +
        (activeStatuses.length > 0 ? 1 : 0) +
        (selectedType !== "all" ? 1 : 0) +
        (selectedOrg !== "all" ? 1 : 0) +
        (selectedCalibration !== "all" ? 1 : 0)

    const clearFilters = () => {
        setSearchText("")
        setActiveStatuses([])
        setSelectedType("all")
        setSelectedOrg("all")
        setSelectedCalibration("all")
    }

    const toggleStatus = (status: string) => {
        setActiveStatuses(prev =>
            prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
        )
    }

    const table = useReactTable({
        data: filteredData,
        columns,
        onSortingChange: setSorting,
        onColumnVisibilityChange: setColumnVisibility,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        initialState: { pagination: { pageSize: 10 } },
        state: { sorting, columnVisibility },
        columnResizeMode: "onChange",
    })

    const currentPage = table.getState().pagination.pageIndex + 1
    const totalPages = table.getPageCount()
    const pageRange = buildPageRange(currentPage, totalPages)

    return (
        <div className="w-full space-y-4">
            {overdueCount > 0 && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-800 px-4 py-2.5 text-sm text-red-700 dark:text-red-400">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span>
                        <span className="font-semibold">{overdueCount}</span> equipo{overdueCount === 1 ? "" : "s"} con calificación vencida. Revisa la columna{" "}
                        <span className="font-semibold">Calificación</span> en la tabla.
                    </span>
                </div>
            )}
            <Card>
                {/* ── Toolbar ── */}
                <CardHeader className="pb-3">
                    <div className="flex flex-col gap-3">
                        {/* Search + action buttons */}
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                <Input
                                    placeholder="Buscar por nombre, serial, marca o cliente..."
                                    value={searchText}
                                    onChange={e => { setSearchText(e.target.value); table.setPageIndex(0) }}
                                    className="pl-9"
                                />
                            </div>

                            {/* Filtros popover */}
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-9 gap-1.5 text-xs cursor-pointer shrink-0 relative">
                                        <Filter className="h-3.5 w-3.5" />
                                        Filtros
                                        {activeFilterCount > 0 && (
                                            <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                                                {activeFilterCount}
                                            </span>
                                        )}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent align="end" className="w-72 p-4 space-y-4">

                                    {/* Estado */}
                                    <div className="space-y-2">
                                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Estado</p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {Object.entries(STATUS_CONFIG).map(([value, cfg]) => {
                                                const isActive = activeStatuses.includes(value)
                                                return (
                                                    <button
                                                        key={value}
                                                        onClick={() => { toggleStatus(value); table.setPageIndex(0) }}
                                                        className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-all cursor-pointer
                                                            ${isActive ? cfg.chipCls : "border-border bg-background text-muted-foreground hover:bg-muted"}`}
                                                    >
                                                        {cfg.label}
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>

                                    {/* Calificación */}
                                    <div className="space-y-2">
                                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Calificación</p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {([
                                                { value: "overdue" as CalibrationStatus, label: "Vencida", activeCls: "border-red-300 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800" },
                                                { value: "soon"    as CalibrationStatus, label: "Próxima", activeCls: "border-amber-300 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800" },
                                                { value: "ok"      as CalibrationStatus, label: "Al día",  activeCls: "border-emerald-300 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800" },
                                            ]).map(({ value, label, activeCls }) => (
                                                <button
                                                    key={value}
                                                    onClick={() => { setSelectedCalibration(prev => prev === value ? "all" : value); table.setPageIndex(0) }}
                                                    className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-all cursor-pointer
                                                        ${selectedCalibration === value ? activeCls : "border-border bg-background text-muted-foreground hover:bg-muted"}`}
                                                >
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Tipo */}
                                    {availableTypes.length > 1 && (
                                        <div className="space-y-2">
                                            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tipo de equipo</p>
                                            <Select value={selectedType} onValueChange={v => { setSelectedType(v); table.setPageIndex(0) }}>
                                                <SelectTrigger className="h-8 text-xs cursor-pointer w-full">
                                                    <SelectValue placeholder="Todos los tipos" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">Todos los tipos</SelectItem>
                                                    {availableTypes.map(t => (
                                                        <SelectItem key={t} value={t}>{EQUIPMENT_TYPE_LABELS[t] ?? t}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}

                                    {/* Cliente */}
                                    {availableOrgs.length > 1 && (
                                        <div className="space-y-2">
                                            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cliente</p>
                                            <Select value={selectedOrg} onValueChange={v => { setSelectedOrg(v); table.setPageIndex(0) }}>
                                                <SelectTrigger className="h-8 text-xs cursor-pointer w-full">
                                                    <SelectValue placeholder="Todos los clientes" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">Todos los clientes</SelectItem>
                                                    {availableOrgs.map(o => (
                                                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}

                                    {/* Limpiar */}
                                    {activeFilterCount > 0 && (
                                        <Button variant="ghost" size="sm" onClick={clearFilters}
                                            className="w-full h-7 gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer">
                                            <X className="h-3 w-3" />
                                            Limpiar filtros ({activeFilterCount})
                                        </Button>
                                    )}
                                </PopoverContent>
                            </Popover>

                            {/* Columnas */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-9 text-xs gap-1.5 cursor-pointer shrink-0">
                                        <Columns2 className="h-3.5 w-3.5" />
                                        Columnas
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    {table.getAllColumns()
                                        .filter(col => col.getCanHide())
                                        .map(col => (
                                            <DropdownMenuCheckboxItem
                                                key={col.id}
                                                checked={col.getIsVisible()}
                                                onCheckedChange={value => col.toggleVisibility(!!value)}
                                            >
                                                {COLUMN_LABELS[col.id] ?? col.id}
                                            </DropdownMenuCheckboxItem>
                                        ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>

                        {/* Result count */}
                        {activeFilterCount > 0 && (
                            <p className="text-xs text-muted-foreground">
                                <span className="font-semibold text-foreground">{filteredData.length}</span> de{" "}
                                <span className="font-semibold text-foreground">{instruments.length}</span> equipos
                            </p>
                        )}
                    </div>
                </CardHeader>

                {/* ── Table ── */}
                <CardContent>
                    <div className="rounded-md border overflow-x-auto">
                        <Table className="table-fixed min-w-120">
                            <TableHeader>
                                {table.getHeaderGroups().map(hg => (
                                    <TableRow key={hg.id}>
                                        {hg.headers.map(header => (
                                            <TableHead key={header.id} style={{ width: header.column.getSize() }}
                                                className="whitespace-nowrap">
                                                {header.isPlaceholder ? null : flexRender(
                                                    header.column.columnDef.header, header.getContext()
                                                )}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows.length ? (
                                    table.getRowModel().rows.map(row => (
                                        <TableRow key={row.id} className="hover:bg-muted/50 transition-colors">
                                            {row.getVisibleCells().map(cell => (
                                                <TableCell key={cell.id} className="overflow-hidden max-w-0 py-3">
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                                            Sin resultados.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>

                {/* ── Pagination ── */}
                <CardFooter>
                    <div className="flex flex-col gap-3 w-full sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-sm text-muted-foreground">
                            {filteredData.length} equipo(s) en total
                        </p>
                        <Pagination className="mx-0 w-auto">
                            <PaginationContent className="flex-wrap gap-1">
                                <PaginationItem>
                                    <PaginationPrevious href="#"
                                        onClick={e => { e.preventDefault(); table.previousPage() }}
                                        aria-disabled={!table.getCanPreviousPage()}
                                        className={!table.getCanPreviousPage() ? "pointer-events-none opacity-50" : ""} />
                                </PaginationItem>
                                {pageRange.map((item, i) =>
                                    item === "ellipsis" ? (
                                        <PaginationItem key={`e-${i}`}><PaginationEllipsis /></PaginationItem>
                                    ) : (
                                        <PaginationItem key={item}>
                                            <PaginationLink href="#" isActive={item === currentPage}
                                                onClick={e => { e.preventDefault(); table.setPageIndex(item - 1) }}>
                                                {item}
                                            </PaginationLink>
                                        </PaginationItem>
                                    )
                                )}
                                <PaginationItem>
                                    <PaginationNext href="#"
                                        onClick={e => { e.preventDefault(); table.nextPage() }}
                                        aria-disabled={!table.getCanNextPage()}
                                        className={!table.getCanNextPage() ? "pointer-events-none opacity-50" : ""} />
                                </PaginationItem>
                            </PaginationContent>
                        </Pagination>
                    </div>
                </CardFooter>
            </Card>
        </div>
    )
}
